import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../../components';
import { LumioMark } from '../../../creatives/components/LumioMark';
import { RunStatusLine } from '../../../creatives/components/StoryboardComposer/RunStatusLine';
import { Root, Title, Lead, Action } from './DraftPlaceholder.styles';

export type DraftPlaceholderProps = {
  /** Lumio is extracting the style (the sheet may have been closed meanwhile). */
  extracting: boolean;
  /** The draft already holds some ingredients (a mood image, a direction or a character). */
  started: boolean;
  /** Open the ingredients sheet. */
  onOpen: () => void;
};

/**
 * A style before its first extraction, in the card where its studio will be: its ingredients live in the sheet, so
 * this says what they are for and opens it again; while Lumio extracts, it says so. Once the definition lands, the
 * studio (the ingredients rail beside the samples) takes its place.
 */
export const DraftPlaceholder = ({ extracting, started, onOpen }: DraftPlaceholderProps): React.ReactElement => {
  const { t } = useTranslation();

  if (extracting) {
    return (
      <Root>
        <RunStatusLine
          stacked
          phrases={[
            t('Reading your mood board'),
            t('Studying the light, materials and colour'),
            t('Writing your style'),
          ]}
        />
        <Lead text="Your samples appear here as soon as your style is written." tag="span" />
        <Action>
          <Button variant="outline" label={t('Show ingredients')} onClick={onOpen} />
        </Action>
      </Root>
    );
  }

  return (
    <Root>
      <LumioMark size={40} />
      <Title text="Start with your ingredients" tag="span" />
      <Lead
        text="Add a mood board, and a direction or characters if you like. Lumio reads them, writes your style and draws four samples in it."
        tag="span"
      />
      <Action>
        <Button label={started ? t('Edit ingredients') : t('Add ingredients')} onClick={onOpen} />
      </Action>
    </Root>
  );
};

export default DraftPlaceholder;
